import React, { useEffect, useState } from 'react';
import { HELP_ARTICLE_LABEL, PLATFORM_LABEL, UserJourney, getUserJourney, screenLabel } from '../../../api/services/activityService';

/**
 * Parcours d'un compte dans l'app (API migration 082) : ses vingt dernières
 * sessions, écran par écran avec le temps passé, et ses réponses à l'aide.
 * Chargé à l'ouverture de la section seulement.
 */

const time = (iso: string) => new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
const day = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
const seconds = (ms: number | null) => {
  if (ms == null) return '';
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)} min ${s % 60 ? `${s % 60} s` : ''}`.trim();
};

const AppJourney = ({ token, userId }: { token: string; userId: string }) => {
  const [data, setData] = useState<UserJourney | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    getUserJourney(token, userId, controller.signal)
      .then((journey) => { setData(journey); setOpen(journey.sessions[0]?.id_session || null); })
      .catch((err) => { if (err.name !== 'AbortError') setError(err.message || 'Parcours indisponible.'); });
    return () => controller.abort();
  }, [token, userId]);

  if (error) return <p className="text-[12.5px] text-red-700">{error}</p>;
  if (!data) return <p className="text-[12.5px] text-gray-400">Chargement…</p>;
  if (data.sessions.length === 0 && data.help.length === 0) {
    return <p className="text-[12.5px] text-gray-500">Aucune session mesurée : la mesure arrive avec la nouvelle version de l’app.</p>;
  }

  return (
    <div className="space-y-3">
      <ol className="space-y-2">
        {data.sessions.map((s) => {
          const expanded = open === s.id_session;
          return (
            <li key={s.id_session} className="rounded-xl border border-gray-100">
              <button
                type="button"
                onClick={() => setOpen(expanded ? null : s.id_session)}
                aria-expanded={expanded}
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-[12.5px]"
              >
                <span className="text-gray-800">
                  <span className="font-medium first-letter:uppercase">{day(s.started_at)}</span> · {time(s.started_at)}
                  <span className="block text-[11px] text-gray-500">
                    {s.platform ? PLATFORM_LABEL[s.platform] : 'Plateforme inconnue'}{s.app_version ? ` ${s.app_version}` : ''}{s.os_version ? ` · système ${s.os_version}` : ''}
                  </span>
                </span>
                <span className="shrink-0 text-right text-[11.5px] tabular-nums text-gray-500">
                  {s.screens.length} écran{s.screens.length > 1 ? 's' : ''}<br />{seconds(s.duration_seconds * 1000) || '—'}
                </span>
              </button>
              {expanded && s.screens.length > 0 && (
                <ol className="border-t border-gray-100 px-3 py-2">
                  {s.screens.map((screen, i) => (
                    <li key={`${screen.at}${i}`} className="flex items-baseline gap-2 py-0.5 text-[12px]">
                      <span className="w-10 shrink-0 tabular-nums text-gray-400">{time(screen.at)}</span>
                      <span className="flex-1 text-gray-800">{screenLabel(screen.screen)}</span>
                      <span className="tabular-nums text-gray-500">{seconds(screen.duration_ms)}</span>
                    </li>
                  ))}
                </ol>
              )}
            </li>
          );
        })}
      </ol>
      {data.help.length > 0 && (
        <div>
          <p className="text-[12px] font-medium text-gray-700">Réponses à l’aide</p>
          <ul className="mt-1 space-y-1">
            {data.help.map((h, i) => (
              <li key={`${h.created_at}${i}`} className="flex justify-between text-[12px]">
                <span className="text-gray-700">{HELP_ARTICLE_LABEL[h.article] || h.article}</span>
                <span className={h.helpful ? 'text-green-700' : 'text-red-700'}>{h.helpful ? 'Oui' : 'Non'} · <span className="text-gray-400">{day(h.created_at)}</span></span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AppJourney;
